export const PREFERRED_LANG_KEY = 'preferred-lang';
/** sessionStorage: this tab's not-yet-saved choices (see PendingChoices). */
export const PENDING_LANG_KEY = 'preferred-lang-pending';
/**
 * localStorage, release migration of this browser's pre-release Spanish choice: '1' once saved (or when there
 * was nothing to carry over); while its save is outstanding, the LegacyMigrationOwner it belongs to.
 */
export const LEGACY_MIGRATED_KEY = 'preferred-lang-migrated';
/** PendingChoices key for a choice made before any application was loaded (e.g. on the login page). */
export const PRE_APPLICATION_KEY = 'pre';

const SUPPORTED = ['en', 'es'] as const;
export type UiLang = typeof SUPPORTED[number];

/**
 * A choice that has not been saved yet. It carries its own value (not the browser-wide boot hint, which
 * another tab can change), the sign-in it belongs to (null before sign-in), and a version so a slower,
 * older save can't clear a newer choice.
 */
export interface PendingChoice {
  lang: UiLang;
  version: number;
  session: string | null;
}

/** Keyed by application id, or PRE_APPLICATION_KEY for a choice not yet tied to an application. */
export type PendingChoices = Record<string, PendingChoice>;

export const asLang = (value: string | null | undefined): UiLang | null =>
  (SUPPORTED as readonly string[]).includes(value ?? '') ? value as UiLang : null;

/**
 * What to do when an application's saved display language arrives (LLS-886 §2): a pending choice for this
 * application wins and is saved; otherwise the saved value is applied; with nothing saved, the application
 * shows English. The browser-wide boot hint is never saved as an application's choice — on a shared
 * browser it may be someone else's.
 */
export function decideOnApplicationLoad(input: { serverLang: string | null; localLang: string | null; pendingLang: string | null }): { apply: UiLang | null; persist: UiLang | null } {
  const server = asLang(input.serverLang);
  const local = asLang(input.localLang);
  const pending = asLang(input.pendingLang);
  if (pending) {
    return { apply: pending === local ? null : pending, persist: pending };
  }
  if (server) {
    return { apply: server, persist: null };
  }
  return { apply: local && local !== 'en' ? 'en' : null, persist: null };
}

/** The application and sign-in the release migration was claimed for; only that one is retried. */
export interface LegacyMigrationOwner {
  applicationId: number;
  lang: UiLang;
  identity: string;
}

export function parseLegacyMigrationOwner(marker: string): LegacyMigrationOwner | null {
  try {
    const owner = JSON.parse(marker);
    return owner && typeof owner.applicationId === 'number' && asLang(owner.lang) && typeof owner.identity === 'string'
      ? owner as LegacyMigrationOwner
      : null;
  } catch {
    return null;
  }
}

/**
 * Who is signed in, from the stored auth data: the token's borrower and company (JWT `sub` + `CompanyGuid`),
 * else the stored user and company, else the record itself. Stays the same across token refresh (which
 * rewrites the token, refresh token and expiry) but changes on sign-in as someone else. Null when signed out.
 */
export function signInIdentity(authData: string | null): string | null {
  if (!authData) {
    return null;
  }
  try {
    const auth = JSON.parse(authData);
    const claims = jwtClaims(auth?.token);
    const user = claims?.['sub'] ?? auth?.userName ?? auth?.userId;
    const company = claims?.['CompanyGuid'] ?? auth?.companyGuid;
    if (user) {
      return `${user}|${company ?? ''}`;
    }
  } catch { /* not JSON: fall through */ }
  return authData;
}

function jwtClaims(token: unknown): Record<string, unknown> | null {
  const payload = typeof token === 'string' ? token.split('.')[1] : undefined;
  if (!payload) {
    return null;
  }
  try {
    return JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
  } catch {
    return null;
  }
}
